import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ProductChatPanel } from "../components/product-chat-panel";
import type { BreadcrumbItem } from "../../../../components/common/breadcrumbs";
import { useProductVariants } from "../../hooks/useProduct";
import { PageContainer } from "../../../../components/common/page-container";
import { ProductDetailsSkeleton } from "../components/product-details-skeleton";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { Breadcrumbs } from "../../../../components/common/breadcrumbs";
import type { ProductVariantDto } from "../../../../api";
import { SimilarProductsSection } from "../components/similar-products-section";
import { ProductDetailsContent } from "../components/product-details-content";
import { ProductNotFound } from "../components/product-not-found";
import { BuyerProductDetailsView } from "../components/buyer-product-details-view";
import { useMe, useMePartner } from "../../../../swr";
import { useCartStore } from "../../../../hooks/stores/useCartStore";
import { showSuccessToast } from "../../../../utils/toasts";
import { Text } from "../../../../components/common/typography";
import { useWishlistStore } from "../../domain/stores/useWishlistStore";
import { useCreateQuotation } from "../../../cart/hooks/useCreateQuotation";
import { Role } from "../../../../api";

export function ProductDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navTrail =
    (location.state as { breadcrumbTrail?: BreadcrumbItem[] } | null)
      ?.breadcrumbTrail ?? null;
  const {
    data: productDetails,
    error,
    isLoading,
  } = useProductVariants(Number(id ?? 0));

  const { isBuyer } = useMePartner();
  const { user } = useMe();
  const isAdmin = user?.role === Role.Admin;
  const addToCart = useCartStore((s) => s.addToCart);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatQuestion, setChatQuestion] = useState<string>();
  const { createQuotation, isLoading: isRequestingQuotation } = useCreateQuotation();

  const baseTemplateProduct = productDetails?.product;
  const variants = useMemo(
    () => productDetails?.variants ?? [],
    [productDetails?.variants]
  );

  const urlVariantId = (() => {
    const raw = searchParams.get("variant");
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  })();
  const selectedVariantId =
    urlVariantId != null && variants.some((v) => v.id === urlVariantId)
      ? urlVariantId
      : null;

  const setSelectedVariantId = useCallback(
    (nextId: number) => {
      const next = new URLSearchParams(searchParams);
      if (String(next.get("variant")) === String(nextId)) return;
      next.set("variant", String(nextId));
      // Preserve location.state so breadcrumbTrail / other nav state survives
      // the implicit URL-replace that seeds `?variant=` on first render.
      setSearchParams(next, { replace: true, state: location.state });
    },
    [searchParams, setSearchParams, location.state],
  );

  // Seed the URL with the first in-stock variant once the fetch resolves, so a
  // cold load still lands on a sensible default and subsequent refreshes keep it.
  useEffect(() => {
    if (selectedVariantId == null && variants.length > 0) {
      const firstInStock = variants.find((v) => (v.supplierStock ?? 0) > 0);
      setSelectedVariantId((firstInStock ?? variants[0]).id);
    }
  }, [variants, selectedVariantId, setSelectedVariantId]);

  const syncLikedStates = useWishlistStore((s) => s.syncLikedStates);
  useEffect(() => {
    if (variants.length > 0) {
      syncLikedStates(
        variants as ReadonlyArray<
          ProductVariantDto & { liked?: boolean | null }
        >,
      );
    }
  }, [variants, syncLikedStates]);

  const selectedVariant: ProductVariantDto | undefined = useMemo(
    () => variants.find((v) => v.id === selectedVariantId) ?? variants[0],
    [variants, selectedVariantId],
  );

  useEffect(() => {
    if (selectedVariant == null) return;
    if (urlVariantId === selectedVariant.id) return;
    const next = new URLSearchParams(searchParams);
    next.set("variant", String(selectedVariant.id));
    setSearchParams(next, { replace: true, state: location.state });
  }, [location.state, searchParams, selectedVariant, setSearchParams, urlVariantId]);

  // Derive a base product object for UI that merges template-level info
  // with the first variant (for fields like image, stock, etc.)
  const baseProduct = useMemo(() => {
    if (!baseTemplateProduct || variants.length === 0) return null;
    const firstVariant = variants[0];
    return {
      ...firstVariant,
      // Override with template-level props where relevant
      id: baseTemplateProduct.id,
      name: baseTemplateProduct.name,
      displayName: baseTemplateProduct.displayName,
      listPrice: baseTemplateProduct.listPrice,
      description: baseTemplateProduct.description,
      standardPrice: baseTemplateProduct.standardPrice,
      type: baseTemplateProduct.type,
      defaultCode: firstVariant.defaultCode ?? baseTemplateProduct.defaultCode,
      category: baseTemplateProduct.category,
      brand: baseTemplateProduct.brand,
      warranty: baseTemplateProduct.warranty,
      warrantyType: baseTemplateProduct.warrantyType,
      countryOfOrigin: baseTemplateProduct.countryOfOrigin,
      datasheetUrl: baseTemplateProduct.datasheetUrl,
      notes: baseTemplateProduct.notes,
      dealType: baseTemplateProduct.dealType,
    };
  }, [baseTemplateProduct, variants]);

  if (isLoading) {
    return (
      <PageContainer>
        <ProductDetailsSkeleton />
      </PageContainer>
    );
  }

  if (error || !productDetails || variants.length === 0 || !baseProduct) {
    return (
      <PageContainer>
        <ProductNotFound error={error} />
      </PageContainer>
    );
  }

  // Preview mode is opened from the seller's product detail page ("Preview"
  // button) so they can see what the buyer sees. Variant switching + image
  // gallery still work; commerce actions are disabled (no cart writes, no
  // navigation to checkout, no quotation request) so the seller can't
  // accidentally place an order against their own product.
  const isPreview = searchParams.get("preview") === "1" || isAdmin;
  const previewNoop = () =>
    showSuccessToast(isAdmin ? "Admin view — buyer actions are disabled." : "Preview mode — buyer actions are disabled.");

  const handleAddToBasket = () => {
    if (isPreview) return previewNoop();
    if (!selectedVariant) return;
    addToCart(selectedVariant, quantity);
    showSuccessToast("Added to cart");
  };

  // "Buy now" — drop-in cart, then jump straight to /checkout. Skips the
  // basket step in the stepper because there's nothing else to review.
  const handleBuyNow = () => {
    if (isPreview) return previewNoop();
    if (!selectedVariant) return;
    addToCart(selectedVariant, quantity);
    navigate(AppRoutes.CHECKOUT);
  };

  const handleRequestQuotation = () => {
    if (isPreview) return previewNoop();
    if (!selectedVariant) return;
    void createQuotation([{ productId: selectedVariant.id, quantity }]);
  };

  const productDescription = baseProduct?.description ?? selectedVariant?.description ?? null;
  const productName = baseProduct?.name ?? selectedVariant?.name ?? "Product";

  // `question` seeds the chat (e.g. a delivery enquiry); omit it for the
  // default product overview.
  const openChat = (question?: string) => {
    setChatQuestion(question);
    setChatOpen(true);
  };

  return (
    <>
    <PageContainer className="max-sm:px-4 max-sm:py-4">
      {isPreview && !isAdmin && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
          <Text variant="bodySm" tone="dark">
            Seller preview — this is exactly what the buyer sees. Add-to-cart, Buy
            now and Request quotation are disabled here.
          </Text>
          <button
            type="button"
            onClick={() => window.close()}
            className="text-textBody hover:text-textDark text-bodySm"
          >
            Close
          </button>
        </div>
      )}
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          ...(navTrail ?? [{ label: "Catalog", path: isAdmin ? AppRoutes.ADMIN_PRODUCTS : AppRoutes.PRODUCTS }]),
          { label: baseProduct.name || "Product" },
        ]}
      />

      {/* Product Details */}
      {(isBuyer || isAdmin || isPreview) && baseTemplateProduct && selectedVariant ? (
        <BuyerProductDetailsView
          baseProduct={baseTemplateProduct}
          variants={variants}
          selectedVariant={selectedVariant}
          onSelectVariant={setSelectedVariantId}
          quantity={quantity}
          onQuantityChange={setQuantity}
          onAddToBasket={handleAddToBasket}
          onBuyNow={handleBuyNow}
          onRequestQuotation={handleRequestQuotation}
          isRequestingQuotation={isRequestingQuotation}
          onChatAbout={() => openChat()}
          onAskAboutDelivery={() =>
            openChat(`What is the estimated delivery time for "${productName}"?`)
          }
        />
      ) : (
        <ProductDetailsContent
          products={variants}
          baseProduct={baseProduct}
          showActions={true}
          productId={baseTemplateProduct?.id}
        />
      )}

      <SimilarProductsSection
        referenceProduct={baseTemplateProduct}
        activeProductId={baseTemplateProduct?.id}
      />
    </PageContainer>
    <ProductChatPanel
      open={chatOpen}
      onClose={() => setChatOpen(false)}
      productId={baseProduct?.id}
      productName={productName}
      productDescription={productDescription}
      productSku={selectedVariant?.defaultCode}
      productPrice={selectedVariant?.listPrice ?? baseProduct?.listPrice}
      productStock={selectedVariant?.supplierStock}
      productBrand={baseProduct?.brand?.name}
      productCategory={baseProduct?.category?.name}
      productImageUrl={selectedVariant?.image ?? baseProduct?.image}
      productNotes={baseProduct?.notes}
      variantId={selectedVariant?.id}
      initialQuestion={chatQuestion}
    />
    </>
  );
}
